import AdminTable from '../../../components/admin/AdminTable';
﻿import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { fetchPrecioPlanServicioHistory } from '../../../services/PrecioPlanServicioHistoryService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { History } from 'lucide-react';

interface HistoryEntry {
    id: number;
    user_email: string;
    user_full_name: string;
    servicio_nombre: string;
    plan_nombre: string;
    action: string;
    timestamp: string;
    changes: any;
}

const PrecioPlanServicioHistoryPage: React.FC = () => {
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [filterUser, setFilterUser] = useState('');
    const [filterAction, setFilterAction] = useState('');
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const [startDate, endDate] = dateRange;
    const itemsPerPage = 10;
    const navigate = useNavigate();

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout } = useAuth();

    useEffect(() => {
        const loadHistory = async () => {
            try {
                const data = await fetchPrecioPlanServicioHistory();
                setHistory(data);
            } catch (error) {
                console.error('Error cargando historial:', error);
            }
        };
        loadHistory();
    }, []);

    const filteredHistory = history.filter(entry => {
        const matchesUser = entry.user_full_name.toLowerCase().includes(filterUser.toLowerCase());
        const matchesAction = filterAction ? entry.action === filterAction : true;
        const entryDate = new Date(entry.timestamp);
    
        // Añade este bloque idéntico al de CategoryHistoryPage
        const matchesDate = () => {
            if (!startDate && !endDate) return true;
            const start = startDate ? new Date(startDate.setHours(0, 0, 0, 0)) : null;
            const end = endDate ? new Date(endDate.setHours(23, 59, 59, 999)) : null;
    
            if (start && end) return entryDate >= start && entryDate <= end;
            if (start) return entryDate >= start;
            if (end) return entryDate <= end;
            return true;
        };
    
        return matchesUser && matchesAction && matchesDate();
    });

    const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedEntries = filteredHistory.slice(startIndex, startIndex + itemsPerPage);

    const renderChanges = (changes: any, action: string) => {
        if (!changes) return 'Sin detalles disponibles';

        const formatValue = (value: any) => {
            if (value === null || value === undefined) return 'N/A';
            if (typeof value === 'boolean') return value ? 'Activo' : 'Inactivo';
            if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
                return new Date(value).toLocaleDateString('es-BO', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
            }
            return value?.toString() || 'N/A';
        };

        if (action === 'created' || action === 'deleted') {
            return (
                <div className="space-y-1">
                    {Object.entries(changes).map(([key, value]) => (
                        <div key={key} className="flex gap-1">
                            <span className="font-medium capitalize">{key.replace('_', ' ')}:</span>
                            <span className="text-gray-600">
                                {formatValue(value)}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }

        if (action === 'updated') {
            return (
                <div className="space-y-1">
                    {Object.entries(changes).map(([key, val]) => {
                        const value = val as { old: any; new: any };
                        return (
                            <div key={key} className="flex flex-col">
                                <span className="font-medium capitalize">{key.replace('_', ' ')}</span>
                                <div className="flex gap-2 text-sm">
                                    <span className="text-red-600 line-through">{formatValue(value.old)}</span>
                                    <span>→</span>
                                    <span className="text-green-600">{formatValue(value.new)}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }

        return 'Sin detalles disponibles';
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [filterUser, filterAction, startDate, endDate]);

    return (
        <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <ExpiredSessionModal
                isOpen={isSessionExpired}
                onClose={async () => {
                    setIsSessionExpired(false);
                    await logout();
                }}
            />

            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Historial de Precios de Planes</h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/priceplan" className="hover:text-blue-600">Precios de Planes</a>
                        <span>/</span>
                        <a href="/priceplan/history" className="text-[#c1348b] hover:text-[#8f1f64]">Historial</a>
                    </div>
                </div>
                <button
                    onClick={() => navigate('/priceplan')}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 flex items-center text-sm font-medium"
                >
                    Volver a Precios de Planes
                </button>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex flex-col md:flex-row gap-4 items-center">
                        <input
                            type="text"
                            placeholder="Filtrar por usuario"
                            className="px-3 py-2 border rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={filterUser}
                            onChange={(e) => setFilterUser(e.target.value)}
                        />

                        <select
                            className="px-3 py-2 border rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={filterAction}
                            onChange={(e) => setFilterAction(e.target.value)}
                        >
                            <option value="">Todas las acciones</option>
                            <option value="created">Creación</option>
                            <option value="updated">Actualización</option>
                            <option value="deleted">Eliminación</option>
                        </select>

                        <DatePicker
                            selectsRange={true}
                            startDate={startDate}
                            endDate={endDate}
                            onChange={(update) => setDateRange(update as [Date | null, Date | null])}
                            isClearable={true}
                            placeholderText="Rango de fechas"
                            locale="es"
                            dateFormat="dd/MM/yyyy"
                            className="px-3 py-2 border rounded-lg text-sm w-72 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            monthsShown={2}
                            showPopperArrow={false}
                            calendarClassName="shadow-lg"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <AdminTable className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acción</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cambios</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {displayedEntries.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <History className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                No hay historial disponible
                                            </span>
                                            <p className="text-gray-500 text-sm">
                                                Realiza acciones en precios de planes para generar historial
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                displayedEntries.map((entry) => (
                                    <tr key={entry.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {entry.user_full_name}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900">
                                            {entry.user_email}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900">
                                            {entry.servicio_nombre}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900">
                                            {entry.plan_nombre}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-medium rounded-full ${entry.action === 'created' ? 'bg-green-100 text-green-800' :
                                                    entry.action === 'updated' ? 'bg-blue-100 text-blue-800' :
                                                        'bg-red-100 text-red-800'
                                                }`}>
                                                {entry.action === 'created' && 'Creación'}
                                                {entry.action === 'updated' && 'Actualización'}
                                                {entry.action === 'deleted' && 'Eliminación'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(entry.timestamp).toLocaleDateString('es-BO', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: false,
                                            })}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                                            {renderChanges(entry.changes, entry.action)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </AdminTable>
                </div>

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    itemsPerPage={itemsPerPage}
                    totalItems={filteredHistory.length}
                />
            </div>
        </div>
    );
};

export default PrecioPlanServicioHistoryPage;
